// features/pets/petsSlice.js
// This slice for fetching pets data from the server and for adding and editing pets to the server

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { db } from '../../app/firebase';
import dayjs from 'dayjs'; 
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  serverTimestamp,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore';

import { getNextRepeatDate } from '../../utils/getNextRepeatDate';
import { getNextRemindDate } from '../../utils/getNextRemindDate';
import { saveCustomEventType } from '../../utils/customEventTypes';
import {
  applyTimeToDate,
  formatTimeHHmm,
  getDeviceTimeZone,
  ownTime,
  parseTimeHHmm,
  remindFormDayFromStored,
  remindSupportsOwnTime,
  resolveRemindDateTime,
  resolveRepeatTime,
  isFutureEventMoment,
} from '../../utils/eventTime';

// Helper to clean event data before saving to DB (remove UI-only fields).
// Время живёт внутри Timestamp-ов (date / repeat / remind / currentRemind), отдельно не пишем.
const cleanEventDataForDB = (eventData) => {
  const {
    eventImageUrl,
    petName,
    _version,
    time,
    repeatTime,
    remindTime,
    currentRemindTime,
    ...cleaned
  } = eventData;
  return cleaned;
};

/** Date with the HH:mm applied; null stays null. */
const withTime = (d, time) => (d ? applyTimeToDate(d, time) : null);

/** `timeZone` marks events whose Timestamps carry a real time (older events are stored at midnight). */
const eventTimeDocFields = (time) => (parseTimeHHmm(time) ? { timeZone: getDeviceTimeZone() } : {});

/** Own remind time for the redux event: '' when it matches the base time or the type has no own time. */
const ownRemindTime = (type, remindTime, baseTime) =>
  remindSupportsOwnTime(type) ? ownTime(remindTime, baseTime) : '';

/**
 * Firestore event → redux event: dates DD.MM.YYYY + times HH:mm.
 * `time` — from `date` (only docs with `timeZone`); `repeatTime` / `remindTime` / `currentRemindTime` —
 * only when they differ from the time of the event they belong to ('' = as event), like the RN form.
 */
const readEventTimes = (data, toDay) => {
  const hasTime = data.timeZone !== undefined;
  const tsDate = (val) => (hasTime && val instanceof Timestamp ? val.toDate() : null);

  const dateTs = tsDate(data.date);
  const time = dateTs ? formatTimeHHmm(dateTs) : null;

  const repeatTs = tsDate(data.repeat);
  const repeatTime = repeatTs ? ownTime(formatTimeHHmm(repeatTs), time) : '';
  const nextTime = resolveRepeatTime(time, repeatTime);

  const remindOf = (val, type, baseTime) => {
    const stored = tsDate(val);
    if (!stored) return { day: toDay(val), time: '' };
    const day = remindFormDayFromStored(stored, type);
    return {
      day: dayjs(day).format('DD.MM.YYYY'),
      time: ownRemindTime(type, formatTimeHHmm(stored), baseTime),
    };
  };
  const remind = remindOf(data.remind, data.remindType, nextTime);
  const currentRemind = remindOf(data.currentRemind, data.currentRemindType, time);

  return {
    time,
    repeatTime,
    remind: remind.day,
    remindTime: remind.time,
    currentRemind: currentRemind.day,
    currentRemindTime: currentRemind.time,
  };
};

// fetching data
export const fetchPets = createAsyncThunk('pets/fetchPets', async (userId) => {
  const userRef = doc(db, 'users', userId);
  const petsRef = collection(userRef, 'pets');
  const snapshot = await getDocs(petsRef);

  const pets = await Promise.all(
    snapshot.docs.map(async (docSnapshot) => {
      const petId = docSnapshot.id;
      const petData = docSnapshot.data();
      
      const eventsRef = collection(db, 'users', userId, 'pets', petId, 'events');
      const eventsSnapshot = await getDocs(eventsRef);

      const events = eventsSnapshot.docs.map((eventDoc) => {
      const data = eventDoc.data();

      const safeToFormattedDate = (val) =>
        val instanceof Timestamp
          ? dayjs(val.toDate()).format('DD.MM.YYYY')
          : typeof val === 'string' && dayjs(val).isValid()
          ? dayjs(val).format('DD.MM.YYYY')
          : null;


      return {
        id: eventDoc.id,
        ...data,
        date: safeToFormattedDate(data.date),
        repeat: safeToFormattedDate(data.repeat),
        ...readEventTimes(data, safeToFormattedDate),
        createdAt: safeToFormattedDate(data.createdAt),
        updatedAt: safeToFormattedDate(data.updatedAt),
      };

    });

    

      return {        
        ...petData,
        createdAt: petData.createdAt?.toDate().toISOString() || null,
        updatedAt: petData.updatedAt?.toDate().toISOString() || null,
        photo: petData.photo || '',
        events,
        id: petId,
      };
    })
  );

  return pets;
});

// Adding new pet to the data base
export const addPet = createAsyncThunk('pets/addPet', async ({ userId, petData }) => {
  const userRef = doc(db, 'users', userId);
  const petsRef = collection(userRef, 'pets');

  const newDoc = await addDoc(petsRef, {
    ...petData,
    photo: petData.photo || '',
    createdAt: serverTimestamp(),
    events: [],
  });

  return {
    
    ...petData,
    createdAt: new Date().toISOString(),
    events: [],
    id: newDoc.id,
  };
});

// Updating pet's data
export const updatePet = createAsyncThunk('pets/updatePet', async ({ userId, petId, petData }) => {
  const petRef = doc(db, 'users', userId, 'pets', petId);
  await updateDoc(petRef, {
    ...petData,
    updatedAt: serverTimestamp(),
  });

  return {
    
    ...petData,
    updatedAt: new Date().toISOString(),
    id: petId,
  };
});

//delete pet
export const deletePet = createAsyncThunk(
  "pets/deletePet",
  async ({ userId, petId }) => {
    const petRef = doc(db, "users", userId, "pets", petId);
    await deleteDoc(petRef);

    return petId;
  }
);

//add event to pet

export const addEvent = createAsyncThunk(
  'pets/addEvent',
  async ({ userId, petId, eventData }) => {
    const eventsRef = collection(db, 'users', userId, 'pets', petId, 'events');

    const parseDate = (str) =>
      str && dayjs(str, 'DD.MM.YYYY').isValid()
        ? dayjs(str, 'DD.MM.YYYY').toDate()
        : null;

    const date = parseDate(eventData.date);
    const repeat = parseDate(eventData.repeat);
    const remind = parseDate(eventData.remind);

    const createdAt = serverTimestamp();

    // Clean event data (remove UI-only fields like eventImageUrl)
    const cleanedEventData = cleanEventDataForDB(eventData);

    // Save custom event type if applicable
    if (cleanedEventData.eventType) {
      await saveCustomEventType(userId, cleanedEventData.eventType);
    }

    // Время события; следующее событие (repeat) и напоминание о нём — со временем повтора
    const time = eventData.time || '';
    const nextTime = resolveRepeatTime(time, eventData.repeatTime);
    const at = (d) => withTime(d, time);
    const atNext = (d) => withTime(d, nextTime);
    const remindAt = (d, type) => resolveRemindDateTime(d, type, time, eventData.remindTime);
    const remindAtNext = (d, type) => resolveRemindDateTime(d, type, nextTime, eventData.remindTime);

    // Будущее: день после сегодня или сегодня, но время ещё не наступило (без времени — по дню)
    const isFutureDate = isFutureEventMoment(eventData.date, time);

    let pastEvent = null;
    let pastEventId = null;

    // 1. Adding past event (only if date is today or in the past)
    if (!isFutureDate) {
      const pastDoc = await addDoc(eventsRef, {
        ...cleanedEventData,
        ...eventTimeDocFields(time),
        date: at(date),
        repeat: atNext(repeat),
        remind: remindAtNext(remind, cleanedEventData.remindType),
        remindType: cleanedEventData.remindType || null,
        repeatType: cleanedEventData.repeatType || null,
        currentRemind: null, // Always null for past events
        status: "past",
        createdAt,
      });

      pastEventId = pastDoc.id;
    }

    // 2. Adding upcoming event(s)
    let upcomingEvent = null;

    if (repeat) {
      const baseDate = dayjs(repeat); // repeat — дата нового события
      let nextRepeat = null;
      let nextRemind = null;

      // 1. Считаем nextRepeat от даты нового события
      if (
        cleanedEventData.repeatType &&
        cleanedEventData.repeatType !== 'Never' &&
        cleanedEventData.repeatType !== 'Custom' &&
        baseDate.isValid()
      ) {
        const repeatDayjs = getNextRepeatDate(cleanedEventData.repeatType, baseDate);
        if (repeatDayjs?.isValid?.()) {
          nextRepeat = repeatDayjs.toDate();

          // 2. Считаем remind от nextRepeat, а не от baseDate
          if (
            cleanedEventData.remindType &&
            cleanedEventData.remindType !== 'Never' &&
            cleanedEventData.remindType !== 'Custom'
          ) {
            const remindDayjs = getNextRemindDate(cleanedEventData.remindType, repeatDayjs);
            if (remindDayjs?.isValid?.()) {
              nextRemind = remindDayjs.toDate();
            }
          }
        }
      }

      // Если был Custom, не устанавливаем repeat/remind в следующем событии
      const nextRepeatType = cleanedEventData.repeatType === 'Custom' ? null : (cleanedEventData.repeatType || null);
      const nextRemindType = cleanedEventData.remindType === 'Custom' ? null : (cleanedEventData.remindType || null);
      const finalNextRepeat = cleanedEventData.repeatType === 'Custom' ? null : nextRepeat;
      const finalNextRemind = cleanedEventData.remindType === 'Custom' ? null : nextRemind;

      const upcomingDoc = await addDoc(eventsRef, {
        ...cleanedEventData,
        ...eventTimeDocFields(nextTime),
        date: atNext(repeat), // дата повтора из прошедшего события

        repeat: atNext(finalNextRepeat),
        remind: remindAtNext(finalNextRemind, nextRemindType),
        repeatType: nextRepeatType,
        remindType: nextRemindType,
        currentRemindType: cleanedEventData.remindType || null,
        currentRemind: remindAtNext(remind, cleanedEventData.remindType), // currentRemind = значение из поля remind формы
        note: '', // Note is not saved for upcoming events
        status: "upcoming",
        createdAt,
        isAutoCreated: true,
        linkedPastEventId: pastEventId,
      });

      upcomingEvent = {
        id: upcomingDoc.id,
        ...cleanedEventData,
        date: eventData.repeat,
        time: nextTime || null,
        repeatTime: '',
        repeat: finalNextRepeat ? dayjs(finalNextRepeat).format('DD.MM.YYYY') : null,
        remind: finalNextRemind ? dayjs(finalNextRemind).format('DD.MM.YYYY') : null,
        remindTime: ownRemindTime(nextRemindType, eventData.remindTime, nextTime),
        repeatType: nextRepeatType,
        remindType: nextRemindType,
        currentRemindType: cleanedEventData.remindType || null,
        currentRemind: eventData.remind, // currentRemind = значение из поля remind формы
        currentRemindTime: ownRemindTime(cleanedEventData.remindType, eventData.remindTime, nextTime),
        medication: cleanedEventData.medication || '',
        note: '', // Note is not saved for upcoming events
        status: "upcoming",
        createdAt: new Date().toISOString(),
        isAutoCreated: true,
        linkedPastEventId: pastEventId,
      };
    } else if (isFutureDate) {
      // For future dates - create only upcoming event (no past event)
      // For standalone upcoming events (created with future date), save note from form
      const upcomingDoc = await addDoc(eventsRef, {
        ...cleanedEventData,
        ...eventTimeDocFields(time),
        date: at(date),
        repeat: null, // No repeat for future events
        remind: null, // No remind for future events (only currentRemind)
        remindType: null, // No remindType for future events
        repeatType: null, // No repeatType for future events
        currentRemindType: cleanedEventData.remindType || null,
        currentRemind: remindAt(remind, cleanedEventData.remindType), // currentRemind = значение из поля remind формы
        note: cleanedEventData.note || '', // Note is saved for standalone upcoming events with future date
        status: "upcoming",
        createdAt,
      });

      upcomingEvent = {
        id: upcomingDoc.id,
        ...cleanedEventData,
        date: eventData.date,
        time: time || null,
        repeatTime: '',
        repeat: null, // No repeat for future events
        remind: null, // No remind for future events (only currentRemind)
        remindTime: '',
        repeatType: null, // No repeatType for future events
        remindType: null, // No remindType for future events
        currentRemindType: cleanedEventData.remindType || null,
        currentRemind: eventData.remind, // currentRemind = значение из поля remind формы
        currentRemindTime: ownRemindTime(cleanedEventData.remindType, eventData.remindTime, time),
        medication: cleanedEventData.medication || '',
        note: cleanedEventData.note || '', // Note is saved for standalone upcoming events with future date
        status: "upcoming",
        createdAt: new Date().toISOString(),
      };
    }

    // returning both events
    if (pastEventId) {
      pastEvent = {
        id: pastEventId,
        ...cleanedEventData,
        date: eventData.date,
        time: time || null,
        repeat: eventData.repeat,
        repeatTime: repeat ? ownTime(nextTime, time) : '',
        remind: eventData.remind,
        remindTime: ownRemindTime(cleanedEventData.remindType, eventData.remindTime, nextTime),
        repeatType: cleanedEventData.repeatType || null,
        currentRemind: null, // Always null for past events
        currentRemindTime: '',
        status: "past",
        createdAt: new Date().toISOString(),
      };
    }

    return {
      petId,
      events: [pastEvent, upcomingEvent].filter(Boolean),
      createdEventId: upcomingEvent?.id || pastEvent?.id,
    };
  }
);

//edit event
export const updateEvent = createAsyncThunk(
  'pets/updateEvent',
  async ({ userId, petId, eventId, eventData }) => {
    const eventRef = doc(db, 'users', userId, 'pets', petId, 'events', eventId);

    const parseDate = (str) =>
      str && dayjs(str, 'DD.MM.YYYY').isValid()
        ? dayjs(str, 'DD.MM.YYYY').toDate()
        : null;

    const date = parseDate(eventData.date);
    const repeat = parseDate(eventData.repeat);
    const remind = parseDate(eventData.remind);
    const currentRemind = parseDate(eventData.currentRemind);

    // Clean event data (remove UI-only fields like eventImageUrl)
    const cleanedEventData = cleanEventDataForDB(eventData);

    // Save custom event type if applicable
    if (cleanedEventData.eventType) {
      await saveCustomEventType(userId, cleanedEventData.eventType);
    }

    // Текущее событие и его напоминание — со временем события; repeat и remind (о следующем) — со временем повтора
    const time = eventData.time || '';
    const nextTime = resolveRepeatTime(time, eventData.repeatTime);

    await updateDoc(eventRef, {
      ...cleanedEventData,
      ...eventTimeDocFields(time),
      date: withTime(date, time),
      repeat: withTime(repeat, nextTime),
      remind: resolveRemindDateTime(remind, cleanedEventData.remindType, nextTime, eventData.remindTime),
      currentRemind:
        resolveRemindDateTime(
          currentRemind,
          cleanedEventData.currentRemindType,
          time,
          eventData.currentRemindTime,
        ) || null,
      updatedAt: serverTimestamp(),
    });

    return {
      petId,
      eventId,
      eventData: {
        ...cleanedEventData,
        date: eventData.date,
        time: time || null,
        repeat: eventData.repeat,
        repeatTime: repeat ? ownTime(nextTime, time) : '',
        remind: eventData.remind,
        remindTime: ownRemindTime(cleanedEventData.remindType, eventData.remindTime, nextTime),
        currentRemind: eventData.currentRemind,
        currentRemindTime: ownRemindTime(
          cleanedEventData.currentRemindType,
          eventData.currentRemindTime,
          time,
        ),
      }
    };
  }
);

// delete event
export const deleteEvent = createAsyncThunk(
  'pets/deleteEvent',
  async ({ userId, petId, eventId }) => {
    const eventRef = doc(db, 'users', userId, 'pets', petId, 'events', eventId);
    await deleteDoc(eventRef);
    return { petId, eventId };
  }
);

// complete upcoming event
export const completeUpcomingEvent = createAsyncThunk(
  'pets/completeUpcomingEvent',
  async ({ userId, petId, eventData }) => {
    const eventRef = doc(db, 'users', userId, 'pets', petId, 'events', eventData.id);

    const parseDate = (str) =>
      str && dayjs(str, 'DD.MM.YYYY').isValid()
        ? dayjs(str, 'DD.MM.YYYY').toDate()
        : null;

    const date = parseDate(eventData.date);
    const repeat = parseDate(eventData.repeat);
    const remind = parseDate(eventData.remind);

    // Clean event data (remove UI-only fields like eventImageUrl)
    const { id: _, ...tempCleaned } = eventData;
    const cleanedEventData = cleanEventDataForDB(tempCleaned);

    // Save custom event type if applicable
    if (cleanedEventData.eventType) {
      await saveCustomEventType(userId, cleanedEventData.eventType);
    }

    // Время события; следующее событие (repeat) и напоминание о нём — со временем повтора
    const time = eventData.time || '';
    const nextTime = resolveRepeatTime(time, eventData.repeatTime);
    const atNext = (d) => withTime(d, nextTime);
    const remindAtNext = (d, type) => resolveRemindDateTime(d, type, nextTime, eventData.remindTime);

    const updatedData = {
      ...cleanedEventData,
      ...eventTimeDocFields(time),
      date: withTime(date, time),
      status: 'past',
      repeat: atNext(repeat),
      remind: remindAtNext(remind, eventData.remindType),
      updatedAt: serverTimestamp(),
      
    };

    await updateDoc(eventRef, updatedData);

    const updatedEventForRedux = {
      ...cleanedEventData,
      id: eventData.id,
      date: eventData.date,
      time: time || null,
      status: 'past',
      repeat: eventData.repeat,
      repeatTime: repeat ? ownTime(nextTime, time) : '',
      remind: eventData.remind,
      remindTime: ownRemindTime(eventData.remindType, eventData.remindTime, nextTime),
      currentRemindTime: '',
      updatedAt: new Date().toISOString(), // ✅ сериализуемое значение
    };

    let newUpcomingEvent = null;

    // если есть repeat, создаём следующее событие
    if (repeat) {
      const baseDate = dayjs(repeat);
      let nextRepeat = null;
      let nextRemind = null;

      if (
        eventData.repeatType &&
        eventData.repeatType !== 'Never' &&
        eventData.repeatType !== 'Custom' &&
        baseDate.isValid()
      ) {
        const repeatDayjs = getNextRepeatDate(eventData.repeatType, baseDate);
        if (repeatDayjs?.isValid?.()) {
          nextRepeat = repeatDayjs.toDate();

          if (
            eventData.remindType &&
            eventData.remindType !== 'Never' &&
            eventData.remindType !== 'Custom'
          ) {
            const remindDayjs = getNextRemindDate(eventData.remindType, repeatDayjs);
            if (remindDayjs?.isValid?.()) {
              nextRemind = remindDayjs.toDate();
            }
          }
        }
      }

      // Если был Custom, не устанавливаем repeat/remind в следующем событии
      const nextRepeatType = eventData.repeatType === 'Custom' ? null : (eventData.repeatType || null);
      const nextRemindType = eventData.remindType === 'Custom' ? null : (eventData.remindType || null);
      const finalNextRepeat = eventData.repeatType === 'Custom' ? null : nextRepeat;
      const finalNextRemind = eventData.remindType === 'Custom' ? null : nextRemind;

      // Remove old currentRemind from cleanedEventData to avoid conflicts
      const { currentRemind: _, ...cleanedDataWithoutCurrentRemind } = cleanedEventData;
      
      const eventsRef = collection(db, 'users', userId, 'pets', petId, 'events');
      const newDoc = await addDoc(eventsRef, {
        ...cleanedDataWithoutCurrentRemind,
        ...eventTimeDocFields(nextTime),
        date: atNext(repeat),
        repeat: atNext(finalNextRepeat),
        remind: remindAtNext(finalNextRemind, nextRemindType),
        repeatType: nextRepeatType,
        remindType: nextRemindType,
        currentRemindType: eventData.remindType || null,
        currentRemind: remindAtNext(remind, eventData.remindType) || null, // currentRemind from form's remind field
        note: '', // Note is not saved for upcoming events
        status: "upcoming",        
        isAutoCreated: true,
        linkedPastEventId: eventData.id,
        createdAt: serverTimestamp(),
        
      });

      newUpcomingEvent = {
        ...cleanedDataWithoutCurrentRemind, 
        id: newDoc.id, 
        date: eventData.repeat,
        time: nextTime || null,
        repeatTime: '',
        repeat: finalNextRepeat ? dayjs(finalNextRepeat).format('DD.MM.YYYY') : null,
        remind: finalNextRemind ? dayjs(finalNextRemind).format('DD.MM.YYYY') : null,
        remindTime: ownRemindTime(nextRemindType, eventData.remindTime, nextTime),
        repeatType: nextRepeatType,
        remindType: nextRemindType,
        currentRemindType: eventData.remindType || null,
        currentRemind: eventData.remind || null, // currentRemind from form's remind field (string format)
        currentRemindTime: ownRemindTime(eventData.remindType, eventData.remindTime, nextTime),
        note: '', // Note is not saved for upcoming events
        status: "upcoming",
        isAutoCreated: true,
        linkedPastEventId: eventData.id,
        createdAt: new Date().toISOString(),
        
      };
    }

    return {
      petId,
      // updatedEventId: eventData.id,
      updatedEvent: updatedEventForRedux,
      newUpcomingEvent,
      createdEventId: newUpcomingEvent?.id || null
    };
  }
);




// Initial state
const initialState = {
  list: [],
  loading: false,
  error: null,
  selectedPetId: null,
  petsNeedRefresh: false,
};

const petsSlice = createSlice({
  name: 'pets',
  initialState,
  reducers: {
    selectPet: (state, action) => {
      state.selectedPetId = action.payload;
    },
    setPetsNeedRefresh: (state, action) => {
      state.petsNeedRefresh = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchPets
      .addCase(fetchPets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPets.fulfilled, (state, action) => {
        state.list = action.payload;
        state.loading = false;
      })
      .addCase(fetchPets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // addPet
      .addCase(addPet.fulfilled, (state, action) => {
        state.list.push(action.payload);
      })

      // updatePet
      .addCase(updatePet.fulfilled, (state, action) => {
        const index = state.list.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = {
            ...state.list[index],
            ...action.payload,
          };
        }
      })

      //deletePet
      .addCase(deletePet.fulfilled, (state, action) => {
        state.list = state.list.filter((pet) => pet.id !== action.payload);
      })

      //addEvent
      .addCase(addEvent.fulfilled, (state, action) => {
        const { petId, events } = action.payload;
        const petIndex = state.list.findIndex(p => p.id === petId);

        if (petIndex !== -1) {
          const pet = state.list[petIndex];
          if (!Array.isArray(pet.events)) {
            pet.events = [];
          }
          pet.events.push(...events);
        } else {
          console.warn("Pet not found in state.list for petId:", petId);
        }
      })

      //edit event
      .addCase(updateEvent.fulfilled, (state, action) => {
        const { petId, eventId, eventData } = action.payload;
        const pet = state.list.find(p => p.id === petId);
        if (pet) {
          const index = pet.events.findIndex(e => e.id === eventId);
          if (index !== -1) {
            pet.events[index] = { id: eventId, ...eventData };
          }
        }
      })

      // deleteEvent
      .addCase(deleteEvent.fulfilled, (state, action) => {
        const { petId, eventId } = action.payload;
        const pet = state.list.find(p => p.id === petId);
        if (pet) {
          pet.events = pet.events.filter(e => e.id !== eventId);
        }
      })

      .addCase(completeUpcomingEvent.fulfilled, (state, action) => {
        console.log("completeUpcomingEvent.fulfilled payload:", action.payload);
        const { petId, updatedEvent, newUpcomingEvent } = action.payload; // ⚠️ новое имя
        const pet = state.list.find(p => p.id === petId);

        if (pet) {
          const idx = pet.events.findIndex(e => e.id === updatedEvent.id);
          if (idx !== -1) {
            pet.events[idx] = updatedEvent; // 💥 полностью заменяем старое событие
          }

          if (newUpcomingEvent) {
            pet.events.push(newUpcomingEvent);
          }
        }
      });


      
  },
});

//exports
export const { selectPet, setPetsNeedRefresh } = petsSlice.actions;

export const selectPetList = (state) => state.pets.list;
export const selectPets = (state) => state.pets;
export const selectSelectedPetId = (state) => state.pets.selectedPetId;


export default petsSlice.reducer;
